import classNames from 'classnames';
import React, { useState, useMemo, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import insightsClient from 'search-insights';

import { TmCart } from '@/tscapi/model/cart';
import { TmCartLineRequest } from '@/tscapi/model/request/cart-request';
import { WebApi } from '@/tscapi/service/webapi.service';
import { Storage } from '@/tscapi/utils/browser-storage';
import { Cookie } from '@/tscapi/utils/cookie';

import { TmFavourite } from '@@/App_Assets/scripts/tscapi/model/favourite';

import { FavouriteUtility } from '../../favorite/favshare.utility';
import { IProduct, IEdp } from '../IProduct';
import NiceSelect from '../NiceSelect/NiceSelect';
import { ProductHelpers } from '../utils/ProductHelpers';
import SizeHelpers, { maxItems, Size } from '../utils/SizeHelpers';
import { SwatchHelpers } from '../utils/SwatchHelpers';

import { en, fr } from './productCardi18n';
import ProductCardModal from './ProductCardModal';

export type ProductCategoryType =
    | 'Clothing & Shoes'
    | 'Jewellery'
    | 'Beauty'
    | 'Home & Garden'
    | 'Kitchen'
    | 'Health & Fitness'
    | 'Electronics'
    | 'Coins & Hobbies';

export type StyleAndSize = {
    DimensionId: string;
    DisplayName: string;
};

export interface ProductCardProps {
    header: {
        title?: string;
        isLiked: boolean;
    };
    productObj: IProduct;
    productHref: string;
    productImage: string;
    availableSwatches: string[];
    priceIsLabel: string;
    name: string;
    brandName: string;
    isPriceRange: string;
    wasPriceRange: string;
    reviewCount: number;
    rating: number;
    styles: Array<StyleAndSize>;
    sizes: Array<StyleAndSize>;
    edps: Array<IEdp>;
    categoryPath: ProductCategoryType;
    enabledAddToCart: boolean;
    isShowstopper: boolean;
    showBadgeImage: boolean;
    badgeImagePath: string;
    itemNo: string;
    edp: number;
    position?: number;
    currentPage?: number;
    pageSize?: number;
    queryId?: string;
    sellerType?: 'TSC' | 'TSCPartner' | 'Marketplace';
    showFraudModal: () => void;
}
type FavoriteEventDetail = {
    productData: IProduct;
    selectedEdp: number;
};

const ProductCard = ({
    availableSwatches,
    header,
    productObj,
    productHref,
    productImage,
    name,
    brandName,
    isPriceRange,
    wasPriceRange,
    reviewCount,
    rating,
    styles,
    sizes,
    edps,
    categoryPath,
    enabledAddToCart,
    isShowstopper,
    showBadgeImage,
    badgeImagePath,
    itemNo,
    priceIsLabel,
    edp,
    position,
    currentPage,
    pageSize,
    queryId,
    sellerType,
    showFraudModal,
}: ProductCardProps) => {
    const { i18n, t } = useTranslation('product-card');
    i18n.addResourceBundle('en', 'product-card', en, true, true);
    i18n.addResourceBundle('fr', 'product-card', fr, true, true);

    const [selectedSizeIndex, setSelectedSizeIndex] = useState(-1);
    const [isLikedCurrent, setIsLikedCurrent] = useState(header.isLiked);
    const [selectedColorOrTasteIndex, setSelectedColorOrTasteIndex] = useState(-1);
    const [isMobileModalEnabled, setIsMobileModalEnabled] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [isAddedToBag, setIsAddedToBag] = useState(false);
    const [isAddToBagLoading, setIsAddToBagLoading] = useState(false);
    const [showSwatches, setShowSwatches] = useState(true);
    const [displayedSizeLabels, setDisplayedSizeLabels] = useState(sizes);
    const [labelSize, setLabelSize] = useState<Size>('md');

    const BREAKPOINT = 1023;
    const SIZE_COUNT_LIMIT = 10;
    const STYLE_COUNT_LIMIT = 16;
    const IMAGE_URL_PREFIX = 'https://item.tscimg.ca/TSC/';
    const IMAGE_URL_SUFFIX = '?impolicy=L';
    const LOADING_ASSET_PATH = '/App_Assets/public/loading.gif';
    const productCardAddButton = useRef<HTMLButtonElement | null>(null);

    //remove brand name from product name if it starts with the brand name
    const lowerName = name.toLowerCase();
    const lowerBrand = brandName.toLowerCase();
    if (lowerName.startsWith(lowerBrand)) {
        name = name.slice(lowerBrand.length).trimStart().replace(/^[. ]+/, '');
    }

    useEffect(() => {
        const body = document.querySelector('body');
        if (isMobileModalEnabled) {
            body.classList.add('bodyScrollOff');
        } else {
            body.classList.remove('bodyScrollOff');
        }
    }, [isMobileModalEnabled]);

    useEffect(() => {
        setIsLikedCurrent(header.isLiked);
    }, [header.isLiked]);

    useEffect(() => {
        const interval = setInterval(() => {
            setWidth(window.innerWidth);
        }, 300);

        return () => {
            clearInterval(interval);
        };
    }, [setWidth]);

    const isMobileOrTablet = useMemo(() => {
        return width <= BREAKPOINT;
    }, [width, BREAKPOINT]);

    const colorOrTasteText = useMemo(() => {
        return t('styles');
    }, [categoryPath, t]);

    function getDefaultEdpItem() {
        return edps.find((edpItem) => edpItem.EdpNo === edp);
    }

    const colorsAndSizesText = useMemo(() => {
        if (styles.length > 0 && sizes.length > 0) {
            return (
                <ul
                    style={{ display: 'flex' }}
                    className="product-card__options-count product-card__options-count--info"
                >
                    <li className="product-card__options-count__item">
                        {styles.length} {colorOrTasteText} |
                    </li>
                    <li className="product-card__options-count__item" style={{ marginLeft: '6px' }}>
                        {' '}
                        {`${sizes.length} ${t('sizes')}`}
                    </li>
                </ul>
            );
        }

        if (styles.length > 0 && sizes.length === 0) {
            return (
                <ul className="product-card__options-count product-card__options-count--info">
                    <li className="product-card__options-count__item">
                        {styles.length} {colorOrTasteText}
                    </li>
                </ul>
            );
        }

        if (styles.length === 0 && sizes.length > 0) {
            return (
                <ul className="product-card__options-count product-card__options-count--info">
                    <li className="product-card__options-count__item">{`${sizes.length} ${t('sizes')}`}</li>
                </ul>
            );
        }

        return (
            <ul className="product-card__options-count product-card__options-count--info">
                <li className="product-card__options-count__item"></li>
            </ul>
        );
    }, [styles, sizes, selectedColorOrTasteIndex, selectedSizeIndex, getDefaultEdpItem, edps, colorOrTasteText, t]);

    const currentProductImage = useMemo(() => {
        if (selectedColorOrTasteIndex === -1) {
            return productImage;
        }

        const edp = edps.find((edpItem) => {
            const sizeIndex = selectedSizeIndex === -1 ? 0 : selectedSizeIndex;
            if (styles[selectedColorOrTasteIndex] && sizes[sizeIndex]) {
                return (
                    edpItem.StyleDimensionId === styles[selectedColorOrTasteIndex].DimensionId &&
                    edpItem.SizeDimensionId === sizes[sizeIndex].DimensionId
                );
            } else if (styles[selectedColorOrTasteIndex]) {
                return edpItem.StyleDimensionId === styles[selectedColorOrTasteIndex].DimensionId;
            } else if (sizes[sizeIndex]) {
                return edpItem.SizeDimensionId === sizes[sizeIndex].DimensionId;
            } else {
                return false;
            }
        });

        if (!edp) {
            return productImage;
        }

        return edp.ImageUrl;
    }, [productImage, selectedColorOrTasteIndex, edps, styles, sizes, selectedSizeIndex]);

    const ratingAsString = useMemo(() => {
        return (Math.round(rating * 2) / 2).toString();
    }, [rating]);

    const ctaText = useMemo(() => {
        if (styles.length === 0 && sizes.length === 0) {
            return t('goToDetailPage');
        }

        if (styles.length === 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1) {
                return t('selectSize');
            }
        }

        if (styles.length !== 0 && sizes.length === 0) {
            if (selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }
        }

        if (styles.length !== 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex === -1) {
                return t('selectSizeAndStyle');
            }

            if (selectedSizeIndex !== -1 && selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }

            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex !== -1) {
                return t('selectSize');
            }
        }

        if (!showSwatches && labelSize === 'select') {
            return t('selectSizeAndStyle');
        }

        return t('goToDetailPage');
    }, [
        isAddedToBag,
        selectedSizeIndex,
        selectedColorOrTasteIndex,
        styles,
        sizes,
        categoryPath,
        showSwatches,
        labelSize,
    ]);

    const buttonText = useMemo(() => {
        if (isAddedToBag) {
            return t('addedToBag');
        }

        if (styles.length === 0 && sizes.length === 0) {
            return t('addToBag');
        }

        if (styles.length === 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1) {
                return t('selectSize');
            }
        }

        if (styles.length !== 0 && sizes.length === 0) {
            if (selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }
        }

        if (styles.length !== 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex === -1) {
                return t('selectSizeAndStyle');
            }

            if (selectedSizeIndex !== -1 && selectedColorOrTasteIndex === -1) {
                return t('selectStyle');
            }

            if (selectedSizeIndex === -1 && selectedColorOrTasteIndex !== -1) {
                return t('selectSize');
            }
        }

        if (!showSwatches && labelSize === 'select') {
            return t('selectSizeAndStyle');
        }

        return t('addToBag');
    }, [
        isAddedToBag,
        selectedSizeIndex,
        selectedColorOrTasteIndex,
        styles,
        sizes,
        categoryPath,
        showSwatches,
        labelSize,
    ]);

    const isSizeDisabled = useCallback(
        (i: number): boolean => {
            return ProductHelpers.isSizeDisabled(i, selectedColorOrTasteIndex, edps, styles, sizes);
        },
        [selectedColorOrTasteIndex, edps, styles, sizes],
    );

    const isFreqAutoDelivery = deriveFreqAutoDelivery(
        sizes,
        styles,
        getDefaultEdpItem,
        selectedSizeIndex,
        edps,
        selectedColorOrTasteIndex,
    );

    const isStyleDisabled = useCallback(
        (i: number): boolean => {
            return ProductHelpers.isStyleDisabled(i, selectedSizeIndex, edps, styles, sizes);
        },
        [selectedSizeIndex, edps, styles, sizes],
    );

    const firstAvailableStyleIndex = useMemo(() => {
        for (let i = 0; i < styles.length; i++) {
            if (!isStyleDisabled(i)) {
                return i;
            }
        }

        return -1;
    }, [styles, isStyleDisabled]);

    const firstAvailableSizeIndex = useMemo(() => {
        for (let i = 0; i < sizes.length; i++) {
            if (!isSizeDisabled(i)) {
                return i;
            }
        }

        return -1;
    }, [sizes, isSizeDisabled]);
    /**
     * Check every edp value.
     * If every AppliedShipping field is equal to ""(empty string)
     * return true
     * else false
     */
    const isFreeShipping = useMemo(() => {
        let isFreeShipping = true;

        for (const edp of edps) {
            if (edp.AppliedShipping !== '') {
                isFreeShipping = false;
            }
        }

        return isFreeShipping;
    }, [edps]);

    const isSOEligible = useMemo(() => {
        let isSOEligible = false;

        for (const edp of edps) {
            if (edp.IsSOEligible == true) {
                isSOEligible = true;
                break;
            }
        }
        return isSOEligible;
    }, [edps]);

    const getSwatchLink = useCallback((imageUrl: string, dimensionId: string): string => {
        const IMAGE_URL_DELIMITER = '/0x0/';
        const [fst, snd] = imageUrl.split(IMAGE_URL_DELIMITER);
        const [id] = snd.split('_');
        const link = `${fst}/${id}_${dimensionId}_SW.jpg`;
        return IMAGE_URL_PREFIX + link;
    }, []);

    const onMouseLeaveOrBlur = () => {
        setSelectedSizeIndex(labelSize === 'select' ? firstAvailableSizeIndex : -1);
        setSelectedColorOrTasteIndex(showSwatches ? -1 : firstAvailableStyleIndex);
        setIsAddedToBag(false);

        if (document.activeElement instanceof HTMLElement) {
            // console.log('%c Is this whats happening?', 'color: purple; font-weight: bold;');
            document.activeElement.blur();
        }
    };

    const onSizeKeyDownListener = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
        if (e.key === 'Enter') {
            // console.log('%c onSizeKeyDownListener parent.', 'color: purple; font-weight: bold;');
            e.preventDefault();
            setSelectedSizeIndex((prev) => (prev === i ? -1 : i));
        }
    };

    const onSizeClickListener = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
        e.preventDefault();
        setSelectedSizeIndex((prev) => (prev === i ? -1 : i));
    };

    const onColorOrTasteKeyDownListener = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
        // console.log('%c onColorOrTasteKeyDownListener parent.', 'color: purple; font-weight: bold;');
        if (e.key === 'Enter') {
            e.preventDefault();
            setSelectedColorOrTasteIndex((prev) => (prev === i ? -1 : i));
        }
    };

    const onColorOrTasteClickListener = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
        e.preventDefault();
        setSelectedColorOrTasteIndex((prev) => (prev === i ? -1 : i));
    };

    const cartEvent = (eventName: string) => {
        if (typeof Event === 'function') {
            //modern browsers
            const event = new Event(eventName);
            return event;
        } else {
            //ie9,10,11
            const event = document.createEvent('Event');
            event.initEvent(eventName, true, true);
            return event;
        }
    };

    const addToBagAction = async (sizeIndex: number, styleIndex: number) => {
        // console.log('%c addToBagAction!', 'color: red; font-weight: bold;');

        const lang = new Cookie().Language.substring(0, 2) || 'en';
        const baseUrl = '/api/v2/' + lang + '/carts';

        const cookie = new Cookie();
        const cartGuid = cookie.CartGuid;

        const cachebleHeaders = {
            'Content-Type': 'application/json',
            'cache-control': 'no-cache',
            currentdatetime: new Cookie().CurrentDate,
        };

        const sizeDimension = sizes[sizeIndex]?.DimensionId || null;
        const styleDimension = styles[styleIndex]?.DimensionId || null;

        const selectedProduct = edps.find((edpItem) => {
            return edpItem.SizeDimensionId === sizeDimension && edpItem.StyleDimensionId === styleDimension;
        });

        if (!selectedProduct) {
            setIsAddToBagLoading(false);
            return false;
        }

        /*
        console.log('%c selectedProduct.EdpNo, selectedProduct:', 'color: red; font-weight: bold;');
        console.log({ selectedProductEdpNo: selectedProduct.EdpNo, selectedProduct });
        */

        // To open the "add-to-bag modal", set these values.
        // @see add-to-bag.component.tsx
        // @see productdetail-base.ts
        const edpAddedToCartEl = document.querySelector<HTMLInputElement>('#edpAddedToCart');
        const qtyAddedToCartEl = document.querySelector<HTMLInputElement>('#qtyAddedToCart');

        const cartlineModel: TmCartLineRequest = new TmCartLineRequest();

        cartlineModel.ItemEDP = selectedProduct.EdpNo;
        cartlineModel.Quantity = 1;

        if (cookie.UserAccessToken != null && cookie.UserAccessToken.Edp != null) {
            cartlineModel.CustomerEdp = cookie.UserAccessToken.Edp;
        }

        const apiSvc = new WebApi();

        const cartSuccess = (cartData: TmCart) => {
            cookie.CartGuid = cartData.CartGuid;
            cookie.CartProductCount = cartData.OrderSummary.TotalQuantity.toString();

            const storage: Storage = new Storage();
            storage.setSessionCart(cartData);

            if (edpAddedToCartEl && qtyAddedToCartEl) {
                edpAddedToCartEl.value = `${selectedProduct.EdpNo}`;
                qtyAddedToCartEl.value = '1';
                edpAddedToCartEl.dispatchEvent(cartEvent('change'));
            }

            // Previous TSC PRP analytics
            window.analyticsData.productChange = {};
            window.analyticsData.productChange.sku = `${selectedProduct.EdpNo}`;
            window.analyticsData.productChange.quantity = 1;
            window.analyticsData.event.eventTitle = 'change-in-cart';
            // New FastTrack analytics
            window.analyticsData.event.eventType = 'product-card-add-to-cart';
            window.analyticsData.product = selectedProduct;
            window.analyticsData.page.selectedEdp = `${selectedProduct.EdpNo}`;
            window.analyticsData.page.selectedEdpData = selectedProduct;

            setIsAddedToBag(true);
            setIsAddToBagLoading(false);

            if (cartData.Buyer && cartData.Buyer.IsFraud) {
                showFraudModal();
            }
        };

        const cartError = () => {
            setIsAddedToBag(false);
            setIsAddToBagLoading(false);
        };

        if (cookie != undefined && cookie.CartGuid != undefined) {
            return apiSvc.addItemToCart(
                cartlineModel,
                function (responseData) {
                    cartSuccess(responseData);
                },
                function (errorData) {
                    cartError();
                },
            );
        } else {
            return apiSvc.createCartAndAddItem(
                cartlineModel,
                function (responseData) {
                    cartSuccess(responseData);
                },
                function (errorData) {
                    cartError();
                },
            );
        }

        // if (!cartGuid) {
        //   try {
        //     await http.post(baseUrl, {
        //       ItemEdp: selectedProduct.EdpNo,
        //       Quantity: 1,
        //     }, {
        //       headers: cachebleHeaders,
        //     });
        //     setIsAddedToBag(true);

        //     if (edpAddedToCartEl && qtyAddedToCartEl) {
        //       edpAddedToCartEl.value = `${selectedProduct.EdpNo}`;
        //       qtyAddedToCartEl.value = "1";
        //       edpAddedToCartEl.dispatchEvent(cartEvent('change'));
        //     }

        //     return true;
        //   } catch (e) {
        //     setIsAddedToBag(false);
        //     return false;
        //   }
        // } else {
        //   try {
        //     await http.post(baseUrl + `/${cartGuid}/cartlines`, {
        //       ItemEdp: selectedProduct.EdpNo,
        //       Quantity: 1,
        //     }, {
        //       headers: cachebleHeaders,
        //     });
        //     setIsAddedToBag(true);

        //     if (edpAddedToCartEl && qtyAddedToCartEl) {
        //       edpAddedToCartEl.value = `${selectedProduct.EdpNo}`;
        //       qtyAddedToCartEl.value = "1";
        //       edpAddedToCartEl.dispatchEvent(cartEvent('change'));
        //     }

        //     return true;
        //   } catch (e) {
        //     setIsAddedToBag(false);
        //     return false;
        //   }
        // }
    };

    const onAddToBagClick = () => {
        if (!isMobileOrTablet) {
            setIsAddToBagLoading(true);
        } else {
            if (styles.length === 0 && sizes.length === 0) {
                setIsAddToBagLoading(true);
            } else {
                setIsMobileModalEnabled(true);
            }
        }
    };

    const applyDesktopHoverFocusAnalytics = () => {
        // New FastTrack analytics
        if (!isMobileOrTablet) {
            window.analyticsData.product = productObj;
            window.analyticsData.event.eventType = 'product card view';
        }
    };

    const onCtaClick = () => {
        if (!isMobileOrTablet) {
            ctaClick(selectedColorOrTasteIndex, selectedSizeIndex);
        } else {
            if (styles.length === 0 && sizes.length === 0) {
                ctaClick(selectedColorOrTasteIndex, selectedSizeIndex);
            } else {
                // New FastTrack analytics
                window.analyticsData.product = productObj;
                window.analyticsData.event.eventType = 'product card view';
                setIsMobileModalEnabled(true);
            }
        }
    };

    const isCtaDisabled = useMemo(() => {
        if (styles.length === 0 && sizes.length === 0) {
            return false;
        }

        if (styles.length === 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1) {
                return true;
            }
        }

        if (styles.length !== 0 && sizes.length === 0) {
            if (selectedColorOrTasteIndex === -1) {
                return true;
            }
        }

        if (styles.length !== 0 && sizes.length !== 0) {
            return selectedSizeIndex === -1 || selectedColorOrTasteIndex === -1;
        }

        return false;
    }, [styles, sizes, selectedSizeIndex, selectedColorOrTasteIndex]);

    useEffect(() => {
        if (isAddToBagLoading && !isMobileModalEnabled) {
            addToBagAction(selectedSizeIndex, selectedColorOrTasteIndex);
        }
    }, [isAddToBagLoading]);

    useEffect(() => {
        if (labelSize === 'select') {
            setDisplayedSizeLabels(sizes);
            setSelectedSizeIndex(firstAvailableSizeIndex);
        } else {
            const limit = maxItems[labelSize];
            setDisplayedSizeLabels(sizes.slice(0, limit));
        }
    }, [labelSize, maxItems, sizes, setDisplayedSizeLabels, setSelectedSizeIndex, firstAvailableSizeIndex]);

    useEffect(() => {
        const size = SizeHelpers.determineSize(sizes);
        setLabelSize(size);
    }, [sizes, setLabelSize]);

    const onFavouriteClick = async () => {
        const uat = new Cookie().UserAccessToken;

        if (!uat) {
            const targetUrlCookie = new Cookie();
            targetUrlCookie.setValue('TSC_TargetUrl', window.location.href, 1);
            window.location.href = '/pages/signin';
            return;
        }

        const userEdp = uat.Edp;

        if (!userEdp) {
            window.location.href = '/pages/signin';
            return;
        }

        if (isLikedCurrent) {
            try {
                const favouriteUtility = new FavouriteUtility();
                await favouriteUtility.removeByItemNumber(itemNo);
                setIsLikedCurrent(false);
            } catch (e) {
                console.log('Failed to unfavourite', e);
            }
        } else {
            try {
                const favourite: TmFavourite = new TmFavourite();
                favourite.ItemNumber = itemNo;
                favourite.ActivityDateTime = new Date();
                const favouriteUtility = new FavouriteUtility();
                await favouriteUtility.add(favourite);
                setIsLikedCurrent(true);

                // New FastTrack analytics
                /*
                const favouritedProduct = edps.find(prod => prod.EdpNo === edp);
                window.analyticsData.product = favouritedProduct;
                */
                window.analyticsData.product = productObj;
                window.analyticsData.event.eventType = 'favourite';
                //trigger TT analytics event
                const event = new CustomEvent<FavoriteEventDetail>('favoriteEvent', {
                    detail: { productData: productObj, selectedEdp: edp },
                });
                document.dispatchEvent(event);
            } catch (error) {
                console.log('Failed to add favourite', error);
            }
        }
    };

    const ctaClick = (styleIndex: number, sizeIndex: number) => {
        let url = productHref;

        if (styles.length === 0 && sizes.length === 0) {
            url = productHref;
        } else if (styles.length > 0 && styleIndex === -1) {
            url = productHref;
        } else if (sizes.length > 0 && sizeIndex === -1) {
            url = productHref;
        } else if (styles.length > 0 && sizes.length > 0 && (sizeIndex === -1 || styleIndex === -1)) {
            url = productHref;
        } else {
            const currentEdp = edps.find((edpItem) => {
                if (sizes.length === 0 && styleIndex !== -1) {
                    return styles[styleIndex].DimensionId === edpItem.StyleDimensionId;
                } else if (styles.length === 0 && sizeIndex !== -1) {
                    return sizes[sizeIndex].DimensionId === edpItem.SizeDimensionId;
                } else {
                    return (
                        styles[styleIndex].DimensionId === edpItem.StyleDimensionId &&
                        sizes[sizeIndex].DimensionId === edpItem.SizeDimensionId
                    );
                }
            });
            if (!currentEdp) {
                url = productHref;
            } else {
                url = productHref + `&edp=${currentEdp.EdpNo}`;
            }
        }
        // New FastTrack analytics
        window.analyticsData.product = productObj;
        window.analyticsData.event.eventType = 'product card step 1';
        window.location.href = url;
    };

    /*
  console.log('%c isMobileModalEnabled:', 'color: brown; font-weight: bold;');
  console.log({ isMobileModalEnabled });
  */

    const trackSearchEventClick = (itemNo: string, position: number) => {
        /*
        console.log('%c insightsClientObject:', 'color: red; font-weight: bold;');
        console.log({ insightsClientObject: {
                eventName: 'Items clicked',
                index: 'products',
                objectIDs: [itemNo],
                positions: [position + 1 + (currentPage - 1) * pageSize],
                queryID: queryId ? queryId : '',
        } });
        */
        insightsClient('clickedObjectIDsAfterSearch', {
            eventName: 'Items clicked',
            index: 'products',
            objectIDs: [itemNo],
            positions: [position + 1 + (currentPage - 1) * pageSize],
            queryID: queryId ? queryId : '',
        });
        // store data for conversion tracking on PDP
        try {
            window.sessionStorage.setItem(
                'TSC_AlgoliaClick',
                JSON.stringify({
                    index: 'products',
                    objectIDs: [itemNo],
                    positions: [position + 1 + (currentPage - 1) * pageSize],
                    queryID: queryId ? queryId : '',
                }),
            );
        } catch (error) {
            // if it fails
        }
    };

    useEffect(() => {
        //initially we checked if all swatches exist but for optimizatin we only now check one
        //if one exists, we assume all exist
        SwatchHelpers.doSwatchesExist(availableSwatches)
            .then((result) => {
                setShowSwatches(result);
            })
            .catch(() => {
                setShowSwatches(false);
            });
    }, [styles, availableSwatches]);

    useEffect(() => {
        if (!showSwatches) {
            setSelectedColorOrTasteIndex(firstAvailableStyleIndex);
        }
    }, [showSwatches, setSelectedColorOrTasteIndex]);

    const getSwatchUrl = (index: number) => {
        const resource = availableSwatches[index];
        if (!resource) {
            return '';
        }
        return IMAGE_URL_PREFIX + resource;
    };

    const getStarClassName = (
        roundedRating: number,
        ratingValue: 1 | 2 | 3 | 4 | 5,
        isRatingWhole: boolean,
        roundedFractional: number,
    ) => {
        /*
    console.log('%c isRatingWhole, roundedFractional:', 'color: red; font-weight: bold;');
    console.log({ isRatingWhole, roundedFractional });
    */
        const roundedDownWholeNumber = Math.floor(roundedRating);
        const className =
            ratingValue === roundedDownWholeNumber + 1 &&
            !isRatingWhole &&
            roundedFractional <= 87 &&
            roundedFractional >= 13
                ? `product-card__rating__star product-card__rating__star__${roundedFractional}-filled`
                : roundedDownWholeNumber >= ratingValue
                  ? 'product-card__rating__star product-card__rating__star__100-filled'
                  : 'product-card__rating__star product-card__rating__star__0-filled';
        return className;
    };

    const getStars = () => {
        /*
    console.log('%c rating:', 'color: red; font-weight: bold;');
    console.log({ rating });
    */
        const splitRating = rating.toString().split('.');
        const integer = parseInt(splitRating[0]);
        const fractional = splitRating[1]
            ? parseInt(splitRating[1].length === 1 ? splitRating[1] + '0' : splitRating[1])
            : 0;
        /*
    console.log('%c integer, fractional:', 'color: blue; font-weight: bold;');
    console.log({ integer, fractional });
    */
        const roundedFractional =
            fractional > 87
                ? 1
                : fractional <= 87 && fractional >= 63
                  ? 0.75
                  : fractional < 63 && fractional >= 38
                    ? 0.5
                    : fractional < 38 && fractional >= 13
                      ? 0.25
                      : 0;
        const roundedRating = integer + roundedFractional;
        /*
    console.log('%c roundedRating:', 'color: green; font-weight: bold;');
    console.log({ roundedRating });
    */
        const isRatingWhole = roundedRating % 1 === 0;
        return (
            <React.Fragment>
                <span className="product-card__rating">
                    <span className={`${getStarClassName(roundedRating, 1, isRatingWhole, roundedFractional * 100)}`}>
                        &nbsp;
                    </span>
                    <span className={`${getStarClassName(roundedRating, 2, isRatingWhole, roundedFractional * 100)}`}>
                        &nbsp;
                    </span>
                    <span className={`${getStarClassName(roundedRating, 3, isRatingWhole, roundedFractional * 100)}`}>
                        &nbsp;
                    </span>
                    <span className={`${getStarClassName(roundedRating, 4, isRatingWhole, roundedFractional * 100)}`}>
                        &nbsp;
                    </span>
                    <span className={`${getStarClassName(roundedRating, 5, isRatingWhole, roundedFractional * 100)}`}>
                        &nbsp;
                    </span>
                </span>
                <p className="visually-hidden">{`${t('rating')} ${rating} ${t('outOf')} 5 ${t('stars')}`}</p>
            </React.Fragment>
        );
    };

    const renderStyleSwatch = useCallback(() => {
        if (!showSwatches) {
            return <></>;
        }

        return styles.slice(0, STYLE_COUNT_LIMIT).map((style, styleIndex) => (
            <button
                className="product-card__color-and-taste-option"
                key={style.DimensionId}
                onKeyDown={(e) => onColorOrTasteKeyDownListener(e, styleIndex)}
                onClick={(e) => onColorOrTasteClickListener(e, styleIndex)}
                disabled={isStyleDisabled(styleIndex)}
                aria-pressed={styleIndex === selectedColorOrTasteIndex}
            >
                <input
                    type="checkbox"
                    id={`${name}-color-or-taste-${style.DimensionId}`}
                    checked={styleIndex === selectedColorOrTasteIndex}
                    disabled={isStyleDisabled(styleIndex)}
                    onChange={() => setSelectedColorOrTasteIndex(styleIndex)}
                />
                <label
                    className="product-card__color-and-taste-option-label"
                    htmlFor={`${name}-color-or-taste-${style.DimensionId}`}
                    style={{ backgroundImage: `url(${getSwatchUrl(styleIndex)})` }}
                >
                    <span className="visually-hidden">
                        {colorOrTasteText} {style.DisplayName}
                    </span>
                </label>
            </button>
        ));
    }, [
        showSwatches,
        styles,
        STYLE_COUNT_LIMIT,
        onColorOrTasteKeyDownListener,
        onColorOrTasteClickListener,
        isStyleDisabled,
        selectedColorOrTasteIndex,
        setSelectedColorOrTasteIndex,
        name,
        SwatchHelpers,
        SwatchHelpers.getSwatchUrl,
        colorOrTasteText,
    ]);

    const selectOnChange = (value: string) => {
        const index = styles.findIndex((style) => style.DimensionId === value);
        setSelectedColorOrTasteIndex(index);
    };

    {
        /*
    const renderStyleDropdown = useCallback(() => {
        if (styles.length === 0) {
        return <></>;
        }

        const stylesOptions = styles.slice(0, STYLE_COUNT_LIMIT).map((style, styleIndex) => {
        return {
            option: style.DisplayName,
            value: style.DimensionId,
            disabled: isStyleDisabled(styleIndex)
        };
        })

        return (
        <select
            className="product-card__color-and-taste__dropdown"
            defaultValue={styles[firstAvailableStyleIndex].DimensionId}
            onLoad={() => setSelectedColorOrTasteIndex(firstAvailableStyleIndex)}
            onChange={(e) => {
            const index = styles.findIndex((style) => {
                return style.DimensionId === e.target.value;
            });
            setSelectedColorOrTasteIndex(index);
            }}
            value={styles[selectedColorOrTasteIndex]?.DimensionId}
        >
            {
            styles.slice(0, STYLE_COUNT_LIMIT).map((style, styleIndex) => (
                <option
                key={style.DimensionId}
                value={style.DimensionId}
                disabled={isStyleDisabled(styleIndex)}
                >
                {style.DisplayName}
                </option>
            ))
            }
        </select>
        );
    }, [styles, setSelectedColorOrTasteIndex, selectedColorOrTasteIndex, isStyleDisabled]);
    */
    }

    const renderStyleDropdown = useCallback(() => {
        if (styles.length === 0) {
            return <></>;
        }

        const stylesOptions = styles.map((style, styleIndex) => {
            return {
                option: style.DisplayName,
                value: style.DimensionId,
                disabled: isStyleDisabled(styleIndex),
            };
        });

        return (
            <NiceSelect
                id="nsColourTaste"
                options={stylesOptions}
                selectedIndex={selectedColorOrTasteIndex}
                selectClassName="product-card__color-and-taste__dropdown"
                selectDefaultValue={styles[firstAvailableStyleIndex]?.DimensionId}
                selectOnLoad={() => setSelectedColorOrTasteIndex(firstAvailableStyleIndex)}
                selectOnChange={selectOnChange}
            />
        );
    }, [styles, setSelectedColorOrTasteIndex, selectedColorOrTasteIndex, isStyleDisabled]);

    const renderStyles = () => {
        return (
            <fieldset className="product-card__main__fieldset">
                <legend className="visually-hidden">{colorOrTasteText}</legend>
                {/* Color and Taste */}
                {styles.length !== 0 && (
                    <div className="product-card__color-and-taste-wrapper">
                        <p className="product-card__color-and-taste-title">
                            {t('selectStyle')}:{' '}
                            {selectedColorOrTasteIndex !== -1 && (
                                <strong>{styles[selectedColorOrTasteIndex].DisplayName}</strong>
                            )}
                        </p>
                        {showSwatches && (
                            <div className="product-card__color-and-taste-items">{renderStyleSwatch()}</div>
                        )}
                        {!showSwatches && renderStyleDropdown()}
                        {styles.length >= STYLE_COUNT_LIMIT && showSwatches && (
                            <a
                                href={productHref}
                                className="product-card__color-and-taste-view-all"
                                onClick={
                                    itemNo && !isNaN(position) && currentPage && pageSize
                                        ? () => trackSearchEventClick(itemNo, position)
                                        : null
                                }
                            >
                                {`${t('viewAll')} ${colorOrTasteText}`}
                            </a>
                        )}
                    </div>
                )}
            </fieldset>
        );
    };

    const renderSizeDropdown = useCallback(() => {
        if (sizes.length === 0) {
            return <></>;
        }

        return (
            <select
                className="product-card__color-and-taste__dropdown"
                defaultValue={sizes[firstAvailableSizeIndex]?.DimensionId}
                // eslint-disable-next-line react/no-unknown-property
                onLoad={() => setSelectedSizeIndex(firstAvailableSizeIndex)}
                onChange={(e) => {
                    const index = sizes.findIndex((size) => {
                        return size.DimensionId === e.target.value;
                    });
                    setSelectedSizeIndex(index);
                }}
                value={sizes[selectedSizeIndex]?.DimensionId}
            >
                {sizes.map((size, sizeIndex) => (
                    <option key={size.DimensionId} value={size.DimensionId} disabled={isSizeDisabled(sizeIndex)}>
                        {size.DisplayName}
                    </option>
                ))}
            </select>
        );
    }, [sizes, firstAvailableSizeIndex, selectedSizeIndex, setSelectedSizeIndex, isSizeDisabled]);

    return (
        <>
            <div
                className="product-card"
                onMouseEnter={applyDesktopHoverFocusAnalytics}
                onFocus={applyDesktopHoverFocusAnalytics}
                onMouseLeave={onMouseLeaveOrBlur}
                // onBlur={onMouseLeaveOrBlur}
                role="group"
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
            >
                <div className="product-card__header">
                    <div className="product-card__header-flex">
                        <span className="product-card__header-title">
                            {priceIsLabel !== '' ? priceIsLabel : ''}
                            {priceIsLabel === '' && <span>&nbsp;</span>}
                        </span>
                        <button
                            className={classNames('product-card__header-like', {
                                'product-card__header-like--liked': isLikedCurrent,
                            })}
                            aria-pressed={isLikedCurrent}
                            onClick={onFavouriteClick}
                        >
                            <span className="visually-hidden">{`${t('like')} ${name}`}</span>
                        </button>
                    </div>
                    <div className="product-card__header-flex2">
                        {sellerType === 'Marketplace' ? (
                            <div className="product-card__seller-type">
                                <img
                                    src="/App_Assets/public/marketplace-icon.svg"
                                    alt="marketplace-icon"
                                    className="product-card__header-title2-icon"
                                />
                                <span className="product-card__header-title-2">{sellerType + ' Seller'}</span>
                            </div>
                        ) : (
                            <span>&nbsp;</span>
                        )}
                    </div>
                </div>
                <a
                    href={`${productHref}&edp=${edp}`}
                    className="product-card__thumbnail"
                    onClick={
                        itemNo && !isNaN(position) && currentPage && pageSize
                            ? () => trackSearchEventClick(itemNo, position)
                            : null
                    }
                    data-lpos="product card image"
                >
                    <img src={IMAGE_URL_PREFIX + currentProductImage + IMAGE_URL_SUFFIX} alt={name} />
                </a>

                <form className="product-card__main">
                    <div className="product-card__option-button">{colorsAndSizesText}</div>

                    <fieldset className="product-card__main__fieldset">
                        <legend className="visually-hidden">{t('sizes')}</legend>

                        {/* Size */}
                        {displayedSizeLabels.length !== 0 && (
                            <div className="product-card__size-wrapper">
                                <span className="product-card__size-title">
                                    {t('selectSize')}:{' '}
                                    {selectedSizeIndex !== -1 && (
                                        <strong>{displayedSizeLabels[selectedSizeIndex].DisplayName}</strong>
                                    )}
                                </span>
                                <div
                                    className={classNames('product-card__size-items', {
                                        'product-card__size-items--sm': labelSize === 'sm',
                                        'product-card__size-items--md': labelSize === 'md',
                                        'product-card__size-items--lg': labelSize === 'lg',
                                        'product-card__size-items--xl': labelSize === 'xl',
                                    })}
                                >
                                    {labelSize === 'select' && renderSizeDropdown()}
                                    {labelSize !== 'select' &&
                                        displayedSizeLabels.map((size, sizeIndex) => (
                                            <button
                                                className="product-card__size-option"
                                                key={size.DimensionId}
                                                onKeyDown={(e) => onSizeKeyDownListener(e, sizeIndex)}
                                                onClick={(e) => onSizeClickListener(e, sizeIndex)}
                                                disabled={isSizeDisabled(sizeIndex)}
                                                aria-pressed={sizeIndex === selectedSizeIndex}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`${name}-size-${size.DimensionId}`}
                                                    checked={sizeIndex === selectedSizeIndex}
                                                    onChange={() => setSelectedSizeIndex(sizeIndex)}
                                                    disabled={isSizeDisabled(sizeIndex)}
                                                    tabIndex={0}
                                                />
                                                <label
                                                    className="product-card__size-option-label"
                                                    htmlFor={`${name}-size-${size.DimensionId}`}
                                                >
                                                    <span className="visually-hidden">Size</span>
                                                    {size.DisplayName}
                                                </label>
                                            </button>
                                        ))}
                                </div>
                                {sizes.length > displayedSizeLabels.length && (
                                    <a
                                        href={productHref}
                                        className="product-card__size-view-all"
                                        onClick={
                                            itemNo && !isNaN(position) && currentPage && pageSize
                                                ? () => trackSearchEventClick(itemNo, position)
                                                : null
                                        }
                                    >
                                        {t('viewAllSizes')}
                                    </a>
                                )}
                            </div>
                        )}
                    </fieldset>

                    {renderStyles()}

                    <fieldset className="product-card__main__fieldset">
                        <legend className="visually-hidden"></legend>

                        {/* Frequency Options */}
                        {isSOEligible && (
                            <div className="product-card__size-wrapper">
                                <span className="product-card__size-title">{t('optionsAvailable')}: </span>
                                <div
                                    className={classNames('product-card__size-items', {
                                        'product-card__size-items--sm': labelSize === 'sm',
                                        'product-card__size-items--md': labelSize === 'md',
                                        'product-card__size-items--lg': labelSize === 'lg',
                                        'product-card__size-items--xl': labelSize === 'xl',
                                    })}
                                >
                                    <div className="product-card__size-option">
                                        <label
                                            className={classNames('product-card__size-option-frequency', {
                                                frequency__disabled: !isFreqAutoDelivery(),
                                            })}
                                        >
                                            {t('autoDelivery')}
                                        </label>
                                    </div>
                                    <div className="product-card__size-option">
                                        <label className="product-card__size-option-frequency">
                                            {t('oneTimeShipment')}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </fieldset>

                    <div className="product-card__info">
                        <a
                            href={productHref}
                            className="product-card__info-pname"
                            onClick={
                                itemNo && !isNaN(position) && currentPage && pageSize
                                    ? () => trackSearchEventClick(itemNo, position)
                                    : null
                            }
                        >
                            {name}
                        </a>
                        <p className="product-card__info-brand">{brandName ? `${brandName}` : '\u00a0'}</p>
                        <div className="product-card__info-price">
                            <a
                                href={productHref}
                                className="product-card__info-price__is-price"
                                style={{
                                    flexDirection:
                                        isPriceRange.includes('-') || wasPriceRange.includes('-') ? 'column' : 'row',
                                }}
                                onClick={
                                    itemNo && !isNaN(position) && currentPage && pageSize
                                        ? () => trackSearchEventClick(itemNo, position)
                                        : null
                                }
                            >
                                <span>
                                    <span className="visually-hidden">{t('currentPrice')}</span>
                                    {isPriceRange}
                                </span>
                                {isPriceRange !== wasPriceRange && (
                                    <span className="product-card__info-price__was-price">
                                        <span className="visually-hidden">{t('previousPrice')}</span>
                                        {wasPriceRange}
                                    </span>
                                )}
                            </a>
                        </div>
                    </div>
                </form>

                <div className="product-card__reviews">
                    {reviewCount !== 0 && (
                        <>
                            {getStars()}
                            <a
                                href={productHref}
                                className="product-card__reviews-count"
                                onClick={
                                    itemNo && !isNaN(position) && currentPage && pageSize
                                        ? () => trackSearchEventClick(itemNo, position)
                                        : null
                                }
                            >
                                {`${reviewCount} ${t('reviews')}`}
                            </a>
                        </>
                    )}
                </div>

                <div id="btnAddToCartComponent"></div>
                <button
                    className="product-card__add-button"
                    ref={productCardAddButton}
                    // disabled={isMobileOrTablet ? false : (!(buttonText === t('addToBag') || buttonText === t('addedToBag')))}
                    disabled={isMobileOrTablet ? false : isCtaDisabled}
                    // onClick={onAddToBagClick}
                    onClick={onCtaClick}
                    aria-haspopup={!isMobileOrTablet ? true : null}
                    aria-controls={!isMobileOrTablet ? 'product-card-overlay' : null}
                    aria-expanded={!isMobileOrTablet ? isMobileModalEnabled : null}
                >
                    {isAddedToBag && (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="16"
                            viewBox="0 0 20 16"
                            className="product-card__add-button__added-icon"
                        >
                            <g fill="none" fillRule="evenodd" stroke="none" strokeLinecap="round" strokeWidth="1">
                                <g
                                    className="chevron chevron-stroke--white"
                                    strokeWidth="2"
                                    transform="translate(-85 -609)"
                                >
                                    <g transform="translate(13 591)">
                                        <path d="M73 26L79.5454545 32 91 19"></path>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    )}
                    <div
                        className="product-card__add-button__loading"
                        style={{ display: isAddToBagLoading ? 'flex' : 'none' }}
                    >
                        <img src={LOADING_ASSET_PATH} alt="" className="product-card__add-button__loading-icon" />
                    </div>
                    {/*<span className="product-card__add-button-title">{buttonText}</span>*/}
                    <span className="product-card__add-button-title">{ctaText}</span>
                </button>
            </div>
            {isMobileModalEnabled && (
                <ProductCardModal
                    colorOrTasteText={colorOrTasteText}
                    productImage={productImage}
                    sizes={sizes}
                    styles={styles}
                    onCloseListener={() => {
                        setIsMobileModalEnabled(false);
                        // console.log('%c setIsMobileModalEnabled #3', 'color: red; font-weight: bold;');
                        setIsAddedToBag(false);
                        /*
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
            */
                        setSelectedSizeIndex(-1);
                        setSelectedColorOrTasteIndex(-1);
                        productCardAddButton?.current?.focus();
                    }}
                    //onAddToBagClickListener={addToBagAction}
                    onAddToBagClickListener={ctaClick}
                    edps={edps}
                    productHref={productHref}
                    categoryPath={categoryPath}
                    updateSizeIndex={(v) => setSelectedSizeIndex(v)}
                    updateColorOrTasteIndex={(v) => setSelectedColorOrTasteIndex(v)}
                    mainIsAddedToBag={isAddedToBag}
                    updateIsAddToBagLoading={(v) => setIsAddToBagLoading(v)}
                    itemNo={itemNo}
                    edp={edp}
                    position={position}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    queryId={queryId}
                    showSwatches={showSwatches}
                    labelSize={labelSize}
                    displayedSizeLabels={displayedSizeLabels}
                    availableSwatches={availableSwatches}
                />
            )}
        </>
    );
};

export default ProductCard;
export function deriveFreqAutoDelivery(
    sizes: StyleAndSize[],
    styles: StyleAndSize[],
    getDefaultEdpItem: () => IEdp,
    selectedSizeIndex: number,
    edps: IEdp[],
    selectedColorOrTasteIndex: number,
) {
    return () => {
        let isFrequencyAutoDelivery = false;
        let currentEdp = null;
        //figure out selected EDP for check below
        if (sizes.length === 0 && styles.length === 0) {
            //has no styles or sizes
            currentEdp = getDefaultEdpItem();
        } else if (sizes.length > 0 && styles.length === 0) {
            // has sizes, no styles
            if (selectedSizeIndex !== -1) {
                // size selected
                currentEdp = edps.find((edpItem) => {
                    return edpItem.SizeDimensionId === sizes[selectedSizeIndex].DimensionId;
                });
            } else {
                // size not selected, return defaultedp
                const tmp = getDefaultEdpItem();
                currentEdp = tmp && tmp.Inventory > 0 && tmp.Inventory < 10 ? tmp : undefined;
            }
        } else if (styles.length > 0 && sizes.length === 0) {
            // has styles, no size
            if (selectedColorOrTasteIndex !== -1) {
                currentEdp = edps.find((edpItem) => {
                    return edpItem.StyleDimensionId === styles[selectedColorOrTasteIndex].DimensionId;
                });
            } else {
                const tmp = getDefaultEdpItem();
                currentEdp = tmp && tmp.Inventory > 0 && tmp.Inventory < 10 ? tmp : undefined;
            }
        } else if (selectedSizeIndex === -1 && selectedColorOrTasteIndex === -1) {
            // has both Style/Size, not selected
            currentEdp = getDefaultEdpItem();
        } else if (selectedSizeIndex === -1 && selectedColorOrTasteIndex !== -1) {
            // has both, style selected
            currentEdp = edps.find((edpItem) => {
                return edpItem.StyleDimensionId === styles[selectedColorOrTasteIndex].DimensionId;
            });
        } else if (selectedSizeIndex !== -1 && selectedColorOrTasteIndex === -1) {
            // has both, size selected
            currentEdp = edps.find((edpItem) => {
                return edpItem.SizeDimensionId === sizes[selectedSizeIndex].DimensionId;
            });
        } else if (selectedSizeIndex !== -1 && selectedColorOrTasteIndex !== -1) {
            // has both Style/Size, both selected
            currentEdp = edps.find((edpItem) => {
                return (
                    edpItem.SizeDimensionId === sizes[selectedSizeIndex].DimensionId &&
                    edpItem.StyleDimensionId === styles[selectedColorOrTasteIndex].DimensionId
                );
            });
        }
        if (currentEdp?.IsSOEligible == true) {
            isFrequencyAutoDelivery = true;
        }
        return isFrequencyAutoDelivery;
    };
}
